/**
 * SPEC-TrainingSimulator §7.3a — tiny, safe markdown renderer for lessons.
 *
 * The app has no markdown dependency, and lessons are admin-authored, so a
 * deliberately small subset is enough: # / ## / ### headings, paragraphs,
 * `-` and `1.` lists, **bold**, *italic*, `code`, and [text](https://url).
 * Everything renders as React elements — no innerHTML, no raw HTML pass-
 * through — so an image caption or lesson body can never inject markup.
 * Shared by the Lessons tab preview (Phase A) and the trainee runner (Phase B).
 */

import { Fragment } from "react";
import { parseBlocks, renderInline } from "@/lib/simple-markdown-parse";

export { parseBlocks, renderInline };

export function SimpleMarkdown({ source, testId }: { source: string; testId?: string }) {
  const blocks = parseBlocks(source);
  const hStyle = { fontFamily: "inherit", fontWeight: 600, margin: "14px 0 6px", lineHeight: 1.25 } as const;
  return (
    <div data-testid={testId} style={{ fontSize: 14, lineHeight: 1.55, color: "#111827" }}>
      {blocks.map((b, i) => {
        if (b.kind === "h") {
          const size = b.level === 1 ? 20 : b.level === 2 ? 17 : 15;
          return <div key={i} role="heading" aria-level={b.level} style={{ ...hStyle, fontSize: size }}>{renderInline(b.text)}</div>;
        }
        if (b.kind === "ul") return <ul key={i} style={{ paddingLeft: 22, margin: "6px 0", listStyle: "disc" }}>{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>;
        if (b.kind === "ol") return <ol key={i} style={{ paddingLeft: 22, margin: "6px 0", listStyle: "decimal" }}>{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ol>;
        return <p key={i} style={{ margin: "6px 0" }}>{renderInline(b.text).map((n, j) => <Fragment key={j}>{n}</Fragment>)}</p>;
      })}
    </div>
  );
}

export default SimpleMarkdown;
